import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useResults } from '@/store/results';
import { useEntitlements } from '@/lib/queries';
import { useSession } from '@/store/session';
import { useCompliance } from '@/store/compliance';
import { Panel } from '@/components/Panel';
import { Metric, MetricGrid } from '@/components/Metric';
import { ScoreGauge } from '@/components/ScoreGauge';
import { SeverityChip } from '@/components/SeverityChip';
import { gradeLabel } from '@/features/oversharing/readiness';
import { IconReport, IconPrint } from '@/components/icons';
import { readToken } from '@/lib/theme';

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * C6 — shareable exec summary. Derived entirely from data already read by C2/C3/C4
 * (no new scope, §4). Print → PDF is client-side via the print stylesheet, so the
 * summary never leaves the browser unless the admin chooses to save/share it.
 */
export function ExecSummary({
  tenantName,
  adminName,
}: {
  tenantName?: string | null;
  adminName?: string | null;
}) {
  const adoption = useResults((s) => s.adoption);
  const readiness = useResults((s) => s.readiness);
  const licenses = useResults((s) => s.licenses);
  const labelCoverage = useResults((s) => s.labelCoverage);
  const compliancePack = useCompliance((s) => s.pack);
  const c2 = useSession((s) => s.hasConsent('C2'));
  const { data: entitlements } = useEntitlements(c2);

  const hasAny =
    adoption ||
    readiness ||
    licenses ||
    labelCoverage ||
    compliancePack ||
    (entitlements && entitlements.length > 0);
  const generatedAt = new Date().toLocaleString();

  const green = readToken('--green', '#3F7057');
  const red = readToken('--red', '#9E4F4F');
  const orange = readToken('--orange', '#A97F3C');
  const grid = readToken('--border', '#D4DDE2');
  const text3 = readToken('--text-3', '#6E7478');

  return (
    <Panel
      id="c6"
      className="print-target"
      icon={<IconReport />}
      title="Executive summary"
      cap="C6"
      why="A one-page, shareable snapshot derived from the modules you've run. No additional permission is requested."
      action={
        hasAny ? (
          <button className="btn btn--secondary btn--sm" onClick={() => window.print()}>
            <IconPrint width={16} height={16} /> Print / Save PDF
          </button>
        ) : undefined
      }
    >
      <p className="small muted" style={{ marginTop: -6 }}>
        {tenantName ? `${tenantName} · ` : ''}{adminName ? `${adminName} · ` : ''}Generated {generatedAt}
      </p>

      {!hasAny && (
        <div className="notice notice--info">
          <strong>Nothing to summarise yet</strong>
          <p>Run any of the modules above — entitlements, adoption, oversharing, licence allocation,
            label coverage, or the compliance audit. Their results roll up here automatically.</p>
        </div>
      )}

      {compliancePack && (
        <>
          <h3 className="metric__label" style={{ marginTop: 12 }}>Compliance posture</h3>
          <div className="gauge">
            <ScoreGauge score={compliancePack.posture.score} size={112} />
            <div className="gauge__body">
              <p className="gauge__grade">
                Grade {compliancePack.posture.grade} · {compliancePack.meta.controlsFailed} failing of{' '}
                {compliancePack.meta.controlsTested}
              </p>
              <p className="gauge__caption">
                Mapped to CIS, NIST, ISO&nbsp;27001, SOC&nbsp;2, HIPAA. {compliancePack.posture.breakdown.critical} critical ·
                {' '}{compliancePack.posture.breakdown.high} high · {compliancePack.posture.breakdown.medium} medium.
              </p>
              <div style={{ marginTop: 8 }}>
                <SeverityChip
                  severity={compliancePack.posture.score >= 80 ? 'pass' : compliancePack.posture.score >= 50 ? 'warn' : 'critical'}
                  label={`Posture ${compliancePack.posture.score}/100`}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {licenses && (
        <>
          <h3 className="metric__label" style={{ marginTop: 12 }}>Licence allocation</h3>
          <MetricGrid>
            <Metric label="Licensed users" value={licenses.totalLicensedUsers} />
            <Metric label="Overlapping plans" value={licenses.usersWithOverlap}
              tone={licenses.usersWithOverlap > 0 ? 'red' : 'green'} />
            <Metric label="Redundant SKUs" value={licenses.usersWithRedundant}
              tone={licenses.usersWithRedundant > 0 ? 'red' : 'green'} />
            <Metric label="Duplicated grants" value={licenses.wastedAssignments}
              tone={licenses.wastedAssignments > 0 ? 'red' : 'green'} />
          </MetricGrid>
        </>
      )}

      {labelCoverage && (
        <>
          <h3 className="metric__label" style={{ marginTop: 12 }}>Sensitivity-label coverage</h3>
          <MetricGrid>
            <Metric label="Sites scanned" value={labelCoverage.sitesScanned} />
            <Metric label="Files scanned" value={labelCoverage.totalFiles} />
            <Metric label="Labelled" value={`${labelCoverage.overallPct}%`}
              tone={labelCoverage.overallPct >= 80 ? 'green' : labelCoverage.overallPct >= 50 ? 'accent' : 'red'} />
            <Metric label="Distinct labels" value={labelCoverage.labels.length}
              tone={labelCoverage.labels.length === 0 ? 'red' : 'default'} />
          </MetricGrid>
        </>
      )}

      {readiness && (
        <>
          <h3 className="metric__label" style={{ marginTop: 12 }}>Copilot oversharing readiness</h3>
          <div className="gauge">
            <ScoreGauge score={readiness.score} size={112} />
            <div className="gauge__body">
              <p className="gauge__grade">{gradeLabel(readiness.score)}</p>
              <p className="gauge__caption">
                {readiness.sitesWithAnonymous} site(s) with "Anyone" links · {readiness.sitesWithOrgWide} org-wide ·
                {' '}{readiness.sitesScanned} scanned.
              </p>
              <div style={{ marginTop: 8 }}>
                <SeverityChip
                  severity={readiness.score >= 80 ? 'pass' : readiness.score >= 50 ? 'warn' : 'critical'}
                  label={`Score ${readiness.score}/100`}
                />
              </div>
            </div>
          </div>
          <div className="chart chart--sm">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { label: 'Anyone links', value: readiness.sitesWithAnonymous, fill: red },
                  { label: 'Org-wide links', value: readiness.sitesWithOrgWide, fill: orange },
                ]}
                margin={{ top: 8, right: 8, bottom: 4, left: -18 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: text3 }} tickLine={false}
                  axisLine={{ stroke: grid }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: text3 }} tickLine={false}
                  axisLine={false} width={30} />
                <Tooltip
                  cursor={{ fill: readToken('--bg-2', '#EFF3F6') }}
                  contentStyle={{
                    borderRadius: 10, border: `1px solid ${grid}`,
                    fontFamily: 'var(--font-mono)', fontSize: 12,
                  }}
                />
                <Bar dataKey="value" name="Sites" radius={[4, 4, 0, 0]}>
                  <Cell fill={red} />
                  <Cell fill={orange} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {adoption && (
        <>
          <h3 className="metric__label" style={{ marginTop: 12 }}>Copilot adoption</h3>
          <MetricGrid>
            <Metric label="Enabled seats" value={adoption.enabledSeats} />
            <Metric label="Active seats" value={adoption.activeSeats} tone="green" />
            <Metric label="Adoption rate" value={pct(adoption.adoptionRate)} tone="accent" />
            <Metric label="Idle seats" value={adoption.idleSeats}
              tone={adoption.idleSeats > 0 ? 'red' : 'green'} />
          </MetricGrid>
          <div className="chart chart--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  dataKey="value"
                  nameKey="name"
                  innerRadius={40}
                  outerRadius={66}
                  paddingAngle={2}
                  data={[
                    { name: 'Active', value: adoption.activeSeats, fill: green },
                    { name: 'Idle', value: adoption.idleSeats, fill: red },
                  ]}
                >
                  <Cell fill={green} />
                  <Cell fill={red} />
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 10,
                    border: `1px solid ${grid}`,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {entitlements && entitlements.length > 0 && (
        <>
          <h3 className="metric__label" style={{ marginTop: 12 }}>Entitlements</h3>
          <table className="matrix">
            <thead><tr><th>Capability</th><th>Status</th></tr></thead>
            <tbody>
              {entitlements.map((e) => (
                <tr key={e.id}>
                  <td className="matrix__label">{e.label}</td>
                  <td>
                    <SeverityChip
                      severity={e.state === 'entitled' ? 'pass' : e.state === 'indeterminate' ? 'warn' : 'info'}
                      label={e.state === 'entitled' ? 'Entitled' : e.state === 'indeterminate' ? 'Indeterminate' : 'Not entitled'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="small muted" style={{ marginTop: 16 }}>
        Generated client-side. No tenant data left your browser to produce this summary.
      </p>
    </Panel>
  );
}
